import type { AuthPermissionResourceRules, AuthPermissionRules } from "../../lib/api/authUsers/rules";
import type {
  AuthPermissionState,
  AuthUserPermission,
  AuthUserPermissionsDetail,
} from "../../lib/api/authUsers/users";
import type { AuthPermissionChange, UpdateAuthUserRequest } from "../../lib/validation/authUsers";

/** CRM detail page query param of the mobile login being edited under its record card. */
export const AUTH_USER_PARAM = "auth_user";

function toChange(permission: AuthUserPermission): AuthPermissionChange {
  if (permission.variant === "full_permissible") return { resource: permission.resource, state: permission.state };
  if (permission.state !== "on") return { resource: permission.resource, state: permission.state };
  return { resource: permission.resource, state: "on", actions: permission.actions, scopes: permission.scopes };
}

/** The form state: the PUT body of one membership's permissions and the login's links, as saved. */
export function requestFromDetail(detail: AuthUserPermissionsDetail, organizationId: string): UpdateAuthUserRequest {
  const membership = detail.memberships.find((candidate) => candidate.organization_id === organizationId);
  return {
    organization_id: organizationId,
    permissions: membership?.permissions.map(toChange) ?? [],
    relations: detail.relations.map(({ relation_type, customer_id }) => ({ relation_type, customer_id })),
  };
}

/** A permission's change for a new state. A detailed permission turned on starts with every action ticked. */
export function changeWithState(
  change: AuthPermissionChange,
  state: AuthPermissionState,
  resourceRules: AuthPermissionResourceRules,
): AuthPermissionChange {
  if (state !== "on") return { resource: change.resource, state };
  if (resourceRules.variant === "full_permissible") return { resource: change.resource, state };
  return change.state === "on"
    ? change
    : { resource: change.resource, state: "on", actions: [...resourceRules.allowed_actions], scopes: [] };
}

/** The body to send: a relation type's links are dropped while its permission is off. */
export function requestToSave(request: UpdateAuthUserRequest, rules: AuthPermissionRules): UpdateAuthUserRequest {
  const onRelationTypes = request.permissions.flatMap((change) => {
    const relation = rules.resources[change.resource].relation;
    return change.state === "on" && relation ? [relation.relation_type] : [];
  });
  return {
    ...request,
    relations: request.relations.filter((relation) => onRelationTypes.includes(relation.relation_type)),
  };
}
